import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  Trash2,
  RefreshCw,
  Search,
  EyeOff,
  CheckCircle,
  Clock,
  ExternalLink,
  Edit,
  X,
} from "lucide-react";
import { server } from "../server";

const DailyUpdatesList = () => {
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editData, setEditData] = useState(null);

  // Fetch all updates
  const fetchUpdates = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${server}/dailyUpdate/all`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      setUpdates(data?.data || []);
      setError("");
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.message || "Failed to fetch updates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUpdates();
  }, []);

  // Delete update
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this update?")) return;
    try {
      await axios.delete(`${server}/dailyUpdate/${id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      setUpdates((prev) => prev.filter((u) => u.id !== id));
    } catch (err) {
      console.error("Delete error:", err);
      alert("Failed to delete update");
    }
  };

  // Toggle Active/Inactive
  const handleToggleActive = async (id, current) => {
    try {
      await axios.patch(
        `${server}/dailyUpdate/${id}`,
        { isActive: !current },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      setUpdates((prev) =>
        prev.map((u) => (u.id === id ? { ...u, isActive: !current } : u))
      );
    } catch (err) {
      console.error("Toggle error:", err);
      alert("Failed to update status");
    }
  };

  // Open Edit Modal
  const handleEdit = (update) => {
    setEditData(update);
    setIsEditModalOpen(true);
  };

  // Handle Edit Form Submit
  const handleUpdateSubmit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await axios.put(
        `${server}/dailyUpdate/${editData.id}`,
        editData,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      setUpdates((prev) =>
        prev.map((u) => (u.id === editData.id ? { ...editData } : u))
      );
      setIsEditModalOpen(false);
    } catch (err) {
      console.error("Update error:", err);
      alert("Failed to update record");
    }
  };

  // Filtered data
  const filtered = updates.filter((u) =>
    u.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "critical":
        return "text-red-400";
      case "high":
        return "text-orange-400";
      case "medium":
        return "text-blue-400";
      case "low":
        return "text-gray-400";
      default:
        return "text-gray-300";
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case "urgent":
        return "text-red-400";
      case "warning":
        return "text-yellow-400";
      case "success":
        return "text-green-400";
      case "info":
        return "text-blue-400";
      default:
        return "text-gray-300";
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold text-white">Daily Updates</h2>
        <div className="flex items-center gap-2 mt-3 sm:mt-0">
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search updates..."
              className="pl-8 pr-3 py-2 border border-gray-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-800 text-white placeholder-gray-400"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <button
            onClick={fetchUpdates}
            className="flex items-center gap-1 bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 transition"
          >
            <RefreshCw size={16} /> Refresh
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 text-red-400 bg-red-400/10 p-3 rounded-lg mb-4 border border-red-400/20">
          <p className="text-sm">{error}</p>
        </div>
      )}

      {/* Loading */}
      {loading ? (
        <div className="flex justify-center items-center py-16">
          <RefreshCw className="animate-spin text-gray-400" size={24} />
        </div>
      ) : (
        <div className="overflow-x-auto bg-gray-800 rounded-xl shadow-lg border border-gray-700">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-700 text-gray-300 uppercase text-xs">
              <tr>
                <th className="px-4 py-3 text-left">#</th>
                <th className="px-4 py-3 text-left">Title</th>
                <th className="px-4 py-3 text-left">Message</th>
                <th className="px-4 py-3 text-left">Type</th>
                <th className="px-4 py-3 text-left">Priority</th>
                <th className="px-4 py-3 text-left">Exams</th>
                <th className="px-4 py-3 text-left">Expiry</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              <AnimatePresence>
                {filtered.length > 0 ? (
                  filtered.map((update, index) => (
                    <motion.tr
                      key={update.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      className="border-b border-gray-700 hover:bg-gray-750 transition-colors"
                    >
                      <td className="px-4 py-3 text-gray-400">{index + 1}</td>
                      <td className="px-4 py-3 font-medium text-white">
                        {update.title}
                      </td>
                      <td className="px-4 py-3 text-gray-300 line-clamp-2 max-w-[200px]">
                        {update.message}
                      </td>
                      <td
                        className={`px-4 py-3 capitalize ${getTypeColor(
                          update.type
                        )}`}
                      >
                        {update.type}
                      </td>
                      <td
                        className={`px-4 py-3 font-semibold ${getPriorityColor(
                          update.priority
                        )}`}
                      >
                        {update.priority}
                      </td>
                      <td className="px-4 py-3 text-gray-300">
                        <div className="flex flex-wrap gap-1">
                          {update.targetExams.slice(0, 2).map((exam, idx) => (
                            <span
                              key={idx}
                              className="px-2 py-1 bg-gray-700 text-gray-300 text-xs rounded-full"
                            >
                              {exam}
                            </span>
                          ))}
                          {update.targetExams.length > 2 && (
                            <span className="px-2 py-1 bg-gray-600 text-gray-400 text-xs rounded-full">
                              +{update.targetExams.length - 2}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 flex items-center gap-1 text-gray-400">
                        <Clock size={14} />{" "}
                        {new Date(update.expiryDate).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() =>
                            handleToggleActive(update.id, update.isActive)
                          }
                          className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold transition-colors ${
                            update.isActive
                              ? "bg-green-500/20 text-green-400 border border-green-500/30 hover:bg-green-500/30"
                              : "bg-gray-700 text-gray-400 border border-gray-600 hover:bg-gray-600"
                          }`}
                        >
                          {update.isActive ? (
                            <>
                              <CheckCircle size={12} /> Active
                            </>
                          ) : (
                            <>
                              <EyeOff size={12} /> Inactive
                            </>
                          )}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex justify-center gap-2">
                          {update.actionUrl && (
                            <a
                              href={update.actionUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="p-1 text-blue-400 hover:bg-blue-500/20 rounded transition-colors"
                              title="Open Link"
                            >
                              <ExternalLink size={16} />
                            </a>
                          )}
                          <button
                            onClick={() => handleEdit(update)}
                            className="p-1 text-green-400 hover:bg-green-500/20 rounded transition-colors"
                            title="Edit"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            onClick={() => handleDelete(update.id)}
                            className="p-1 text-red-400 hover:bg-red-500/20 rounded transition-colors"
                            title="Delete"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </motion.tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={9}
                      className="text-center py-8 text-gray-400 italic"
                    >
                      No updates found.
                    </td>
                  </tr>
                )}
              </AnimatePresence>
            </tbody>
          </table>
        </div>
      )}

      {/* ✅ Edit Modal */}
      <AnimatePresence>
        {isEditModalOpen && (
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-gray-800 border border-gray-700 rounded-xl p-6 w-full max-w-lg shadow-lg relative"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
            >
              <button
                onClick={() => setIsEditModalOpen(false)}
                className="absolute top-3 right-3 text-gray-400 hover:text-white transition-colors"
              >
                <X size={18} />
              </button>
              <h3 className="text-lg font-semibold text-white mb-4">
                Edit Update
              </h3>

              <form onSubmit={handleUpdateSubmit} className="space-y-3">
                <input
                  type="text"
                  placeholder="Title"
                  value={editData?.title || ""}
                  onChange={(e) =>
                    setEditData({ ...editData, title: e.target.value })
                  }
                  className="w-full border border-gray-600 rounded-lg p-2 text-sm bg-gray-700 text-white placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  required
                />
                <textarea
                  placeholder="Message"
                  value={editData?.message || ""}
                  onChange={(e) =>
                    setEditData({ ...editData, message: e.target.value })
                  }
                  className="w-full border border-gray-600 rounded-lg p-2 text-sm h-20 bg-gray-700 text-white placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 resize-none"
                  required
                />
                <input
                  type="text"
                  placeholder="Type"
                  value={editData?.type || ""}
                  onChange={(e) =>
                    setEditData({ ...editData, type: e.target.value })
                  }
                  className="w-full border border-gray-600 rounded-lg p-2 text-sm bg-gray-700 text-white placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                />
                <input
                  type="text"
                  placeholder="Priority"
                  value={editData?.priority || ""}
                  onChange={(e) =>
                    setEditData({ ...editData, priority: e.target.value })
                  }
                  className="w-full border border-gray-600 rounded-lg p-2 text-sm bg-gray-700 text-white placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                />
                <input
                  type="text"
                  placeholder="Target Exams (comma separated)"
                  value={editData?.targetExams?.join(", ") || ""}
                  onChange={(e) =>
                    setEditData({
                      ...editData,
                      targetExams: e.target.value
                        .split(",")
                        .map((x) => x.trim()),
                    })
                  }
                  className="w-full border border-gray-600 rounded-lg p-2 text-sm bg-gray-700 text-white placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                />
                <input
                  type="url"
                  placeholder="Action URL"
                  value={editData?.actionUrl || ""}
                  onChange={(e) =>
                    setEditData({ ...editData, actionUrl: e.target.value })
                  }
                  className="w-full border border-gray-600 rounded-lg p-2 text-sm bg-gray-700 text-white placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                />
                <input
                  type="date"
                  value={
                    editData?.expiryDate
                      ? new Date(editData.expiryDate)
                          .toISOString()
                          .split("T")[0]
                      : ""
                  }
                  onChange={(e) =>
                    setEditData({ ...editData, expiryDate: e.target.value })
                  }
                  className="w-full border border-gray-600 rounded-lg p-2 text-sm bg-gray-700 text-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                />

                <div className="flex justify-end gap-2 pt-4 border-t border-gray-700">
                  <button
                    type="button"
                    onClick={() => setIsEditModalOpen(false)}
                    className="px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Save Changes
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DailyUpdatesList;
